import { useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableContainer,
  useMediaQuery,
} from '@mui/material';
import { useAppI18n } from '../../i18n/use-app-i18n';
import { formatValue } from '../../runtime/formatting';
import type { TableRendererProps } from './table/table-renderer-props.ts';
import { useTableRenderer } from './table/use-table-renderer.ts';
import {
  TableHeader,
  TableBody,
  TableFooter,
  TableToolbar,
  TableStateView,
} from './table/index.ts';
import { TableResponsiveCards } from './table/TableResponsiveCards.tsx';
import { tableTokens } from './table/table-tokens.ts';
import { formatColumnValue } from './table/table-value-format.ts';

export type {
  TableRendererProps,
  TableActionContext,
  TableSavedViewOption,
} from './table/table-renderer-props.ts';

/** Global search is opt-in on the resolved descriptor. */
export function isGlobalSearchCapabilityEnabled(contract: TableRendererProps['contract']): boolean {
  return contract.capabilities?.globalSearch === true;
}

export function TableRenderer(props: TableRendererProps) {
  const {
    className,
    virtualize,
    virtualizeOverscan,
    presentation = 'standalone',
    minHeight,
    actionNode,
  } = props;
  const { text } = useAppI18n();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tr = useTableRenderer(props);
  const effectiveVirtualize = virtualize ?? tr.tableData.length > 50;
  const embedded = presentation === 'embedded';
  const resolvedMinHeight = minHeight ?? (embedded ? 0 : 400);
  const density = tableTokens.density[tr.state.density] ?? tableTokens.density.standard;
  const responsiveMode = tr.contract.responsive?.mode ?? 'scroll';
  const responsiveBreakpoint = tr.contract.responsive?.breakpoint ?? tableTokens.breakpoints.tablet;
  const useResponsivePresentation =
    useMediaQuery(`(max-width:${responsiveBreakpoint}px)`) && responsiveMode !== 'scroll';
  const useResponsiveStateOnlyTable = useResponsivePresentation && tr.tableData.length === 0;
  const getRowIdValue = useCallback(
    (row: Record<string, unknown>) => {
      const raw = props.getRowId
        ? props.getRowId(row)
        : row[tr.contract.rowIdField ?? props.rowIdField ?? 'id'];
      return raw == null ? undefined : String(raw);
    },
    [props.getRowId, props.rowIdField, tr.contract.rowIdField]
  );

  const toolbarDescriptor = useMemo(() => {
    const configuredScopes = tr.contract.export?.scopes ?? ['page', 'selection'];
    const scopes = configuredScopes.filter(
      scope =>
        (scope !== 'all' || Boolean(props.onExport) || Boolean(tr.contract.export?.serverExport)) &&
        (scope !== 'selection' || tr.contract.capabilities?.selection === true)
    );
    if (
      scopes.length === configuredScopes.length &&
      scopes.every((scope, index) => scope === configuredScopes[index])
    ) {
      return tr.contract;
    }
    return {
      ...tr.contract,
      export: {
        ...tr.contract.export,
        scopes,
      },
    };
  }, [props.onExport, tr.contract]);

  const localCsvAllowed =
    tr.contract.privacyAndExportPolicy?.auditExports !== true &&
    (tr.contract.export?.formats === undefined || tr.contract.export.formats.includes('csv')) &&
    (toolbarDescriptor.export?.scopes === undefined ||
      toolbarDescriptor.export.scopes.some(scope => scope === 'page' || scope === 'selection'));
  const exportEnabled =
    tr.contract.capabilities?.export === true &&
    (Boolean(props.onExport) || Boolean(tr.contract.export?.serverExport) || localCsvAllowed);

  const groupSummary = useMemo(() => {
    const groups = tr.groupMetadata?.groups;
    if (!Array.isArray(groups) || tr.state.grouping.length === 0) return [];
    const activeGroup = tr.state.grouping[0];
    const column = tr.contract.columns.find(candidate => candidate.field === activeGroup.field);
    return groups.flatMap(group => {
      if (!group || typeof group !== 'object') return [];
      const entry = group as Record<string, unknown>;
      const values = entry.values;
      const value =
        values && typeof values === 'object'
          ? (values as Record<string, unknown>)[activeGroup.field]
          : undefined;
      const count = typeof entry.count === 'number' ? entry.count : undefined;
      if (value === undefined || count === undefined) return [];
      const formattedValue = column
        ? formatColumnValue(value, column, { locale: tr.resolvedLocale })
        : String(value);
      const formattedCount = formatValue(
        count,
        { formatType: 'integer' },
        { locale: tr.resolvedLocale }
      );
      const label = column ? text(column.labelKey) : activeGroup.field;
      return [`${label}: ${formattedValue} (${formattedCount})`];
    });
  }, [tr.contract.columns, tr.groupMetadata, tr.resolvedLocale, tr.state.grouping, text]);

  const surfaceSx = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    minHeight: resolvedMinHeight,
    overflow: 'hidden',
    border: embedded ? 0 : undefined,
    borderRadius: embedded ? 0 : undefined,
    boxShadow: 'none',
  } as const;

  if (tr.displayLoading && tr.tableData.length === 0) {
    return (
      <Paper className={className} variant="outlined" aria-busy="true" sx={surfaceSx}>
        <Box
          role="status"
          aria-label={text('common.loading')}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            minHeight: embedded ? 160 : resolvedMinHeight,
            p: 3,
          }}
        >
          <CircularProgress aria-hidden="true" />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      className={className}
      variant="outlined"
      aria-busy={tr.displayLoading || undefined}
      sx={surfaceSx}
    >
      <TableToolbar
        descriptor={toolbarDescriptor}
        state={tr.state}
        totalCount={tr.totalCount}
        selectedCount={tr.state.selection.selectedIds.length}
        loading={tr.displayLoading}
        onRefresh={tr.handleRetry}
        onFilterRowToggle={tr.handleToggleFilterRow}
        onClearFilters={tr.handleResetFilters}
        onColumnVisibilityChange={tr.handleColumnVisibilityChange}
        onDensityChange={tr.handleDensityChange}
        onGroupingChange={tr.handleGroupingChange}
        onAggregationsChange={tr.handleAggregationsChange}
        onCreateAction={tr.handleCreateAction}
        onPageAction={tr.handlePageAction}
        onBulkAction={tr.handleBulkAction}
        onSavedViewSelect={tr.handleSavedViewSelect}
        onSavedViewSave={tr.handleSaveView}
        onSavedViewDelete={tr.handleSavedViewDelete}
        onResetLayout={tr.handleResetLayout}
        onResetAll={tr.handleResetAll}
        showFilterRow={tr.showFilters}
        availableViews={tr.savedViews.map(view => ({
          ...view,
          isDefault: view.isDefault ?? false,
          isShared: view.isShared ?? false,
        }))}
        currentViewId={tr.currentViewId}
        unsavedChanges={tr.state.hasUnsavedChanges}
        showGlobalSearch={isGlobalSearchCapabilityEnabled(tr.contract)}
        onGlobalSearchChange={tr.handleGlobalSearchChange}
        bulkActions={tr.contract.bulkActions ?? []}
        pageActions={tr.contract.pageActions ?? []}
        createAction={tr.contract.createAction ?? null}
        exportEnabled={exportEnabled}
        onExport={
          tr.handleExport as (options: {
            scope: 'page' | 'selection' | 'all';
            format: 'csv' | 'xlsx';
            fields?: string[];
          }) => void
        }
        actionNode={actionNode}
      />

      {tr.displayError ? (
        <TableStateView state="error" error={tr.displayError} onRetry={tr.handleRetry} />
      ) : (
        <>
          {tr.displayStale && (
            <Alert severity="warning" sx={{ mb: 0, borderRadius: 0 }}>
              {text('common.states.stale.message')}
            </Alert>
          )}
          {tr.displayDegraded && (
            <Alert severity="info" sx={{ mb: 0, borderRadius: 0 }}>
              {text('common.states.degraded.message')}
            </Alert>
          )}
          {groupSummary.length > 0 && (
            <Alert
              severity="info"
              data-testid="table-group-summary"
              sx={{ mb: 0, borderRadius: 0 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {groupSummary.map(label => (
                  <Chip key={label} size="small" label={label} />
                ))}
              </Box>
            </Alert>
          )}

          {useResponsivePresentation && tr.tableData.length > 0 ? (
            <TableResponsiveCards
              descriptor={tr.contract}
              data={tr.tableData}
              state={tr.state}
              evalCtx={tr.evalCtx}
              locale={tr.resolvedLocale}
              mode={responsiveMode as 'cards' | 'collapse'}
              getRowId={getRowIdValue}
              referenceDisplayMap={tr.referenceDisplayMap}
              onAction={tr.handleRowAction}
              onRowActivate={tr.handleRowActivate}
              selectionEnabled={tr.selectionEnabled}
              onToggleRowSelection={tr.handleToggleRowSelection}
            />
          ) : (
            <TableContainer
              ref={tableContainerRef}
              sx={{
                border: 0,
                borderRadius: 0,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                contain: 'inline-size',
                ...(effectiveVirtualize
                  ? { overflow: 'auto', flex: '1 1 auto' }
                  : { overflowX: 'auto' }),
              }}
            >
              <Table
                size={tr.state.density === 'compact' ? 'small' : 'medium'}
                aria-label={text(tr.contract.titleKey)}
                stickyHeader={!useResponsiveStateOnlyTable}
                data-density={tr.state.density}
                sx={{
                  tableLayout: tr.contract.layout ?? 'auto',
                  minWidth: useResponsiveStateOnlyTable ? '100%' : tr.tableMinWidth || undefined,
                  '& .MuiTableCell-root': {
                    height: density.rowHeight,
                    py: `${density.cellPaddingY}px`,
                    px: `${density.cellPaddingX}px`,
                  },
                  '& .MuiTableCell-head': {
                    height: density.headerHeight,
                  },
                }}
              >
                {!useResponsiveStateOnlyTable && <TableHeader
                  descriptor={tr.contract}
                  state={tr.state}
                  onSortChange={tr.handleSortChange}
                  onColumnResize={tr.handleColumnResize}
                  onColumnReorder={tr.handleColumnReorder}
                  onColumnVisibilityChange={tr.handleColumnVisibilityChange}
                  onColumnPin={tr.handleColumnPin}
                  onColumnUnpin={tr.handleColumnUnpin}
                  pinnedLeft={tr.pinnedLeft}
                  pinnedRight={tr.pinnedRight}
                  showFilterRow={tr.showFilters}
                  onToggleFilterRow={tr.handleToggleFilterRow}
                  onFilterSet={tr.handleFilterSet}
                  onFilterRemove={tr.handleFilterRemove}
                  selectionEnabled={tr.selectionEnabled}
                  allPageSelected={tr.allPageSelected}
                  indeterminate={tr.indeterminate}
                  onSelectAll={tr.handleSelectAll}
                  hasRowActions={(tr.contract.rowActions ?? []).length > 0}
                  locale={tr.resolvedLocale}
                />}

                <TableBody
                  descriptor={tr.contract}
                  data={tr.tableData}
                  state={tr.state}
                  evalCtx={tr.evalCtx}
                  locale={tr.resolvedLocale}
                  loading={tr.displayLoading}
                  error={tr.displayError ?? null}
                  stale={tr.displayStale}
                  degraded={tr.displayDegraded}
                  onRetry={tr.handleRetry}
                  onAction={tr.handleRowAction}
                  onRowActivate={tr.handleRowActivate}
                  rowActivationAction={tr.rowActivationAction}
                  rowIdField={props.rowIdField ?? 'id'}
                  getRowId={props.getRowId}
                  referenceDisplayMap={tr.referenceDisplayMap}
                  onToggleRowSelection={tr.handleToggleRowSelection}
                  aggregates={tr.aggregates}
                  groupMetadata={tr.groupMetadata}
                  scrollRef={tableContainerRef}
                  virtualize={effectiveVirtualize}
                  virtualizeOverscan={virtualizeOverscan}
                />
              </Table>
            </TableContainer>
          )}

          {tr.showPagination && (
            <TableFooter
              descriptor={tr.contract}
              state={tr.state}
              totalCount={tr.totalCount}
              onPageChange={tr.handlePageChange}
              onPageSizeChange={tr.handlePageSizeChange}
            />
          )}
        </>
      )}
    </Paper>
  );
}
